import React from 'react';
import { Menu } from '@headlessui/react';

import { highlightText } from 'utils/common';
import { ClinicUserRole, useUser } from 'context/userContext';
import { getLocationName } from './utils';
import Option from './Option';
import { groupBy, orderBy } from 'lodash';
import ExpandWrapper from 'components/ExpandWrapper/ExpandWrapper';
import { OTHER_GROUP_NAME, ROLE } from 'utils/constants';

const groupByPath = (item: ClinicUserRole) => item.clinic?.clinicGroup?.name;

const ListClinic = ({
  clinicUserRoles,
  searchingValue,
  onClick,
}: {
  clinicUserRoles: ClinicUserRole[];
  searchingValue: string;
  onClick: (clinicUserRole: ClinicUserRole) => void;
}) => {
  return (
    <>
      {clinicUserRoles.map((clinicUserRole) => {
        if (!clinicUserRole.clinic) {
          return null;
        }

        const clinicName = clinicUserRole.clinic.name || '';

        const parts = highlightText(clinicName, searchingValue);

        return (
          <Menu.Item
            key={clinicUserRole.clinic.id}
            as="button"
            className="w-full"
            onClick={() => onClick(clinicUserRole)}
          >
            <Option
              option={{
                avatar: clinicUserRole.clinic.avatar,
                name: clinicUserRole.clinic.name,
                description: getLocationName(clinicUserRole.clinic.location),
              }}
              highlightedParts={parts}
            />
          </Menu.Item>
        );
      })}
    </>
  );
};

const ClinicOptions = ({
  clinicUserRoles,
  searchingValue,
  onClick,
}: {
  clinicUserRoles: ClinicUserRole[];
  searchingValue: string;
  onClick: (clinicUserRole: ClinicUserRole) => void;
}) => {
  const { user } = useUser();
  const isGlobalAdmin = user.adminRole?.role === ROLE.GLOBAL_ADMIN;

  const isNotAnyClinicCreated =
    clinicUserRoles.filter((item) => item.clinic === null).length === 1;

  if (isNotAnyClinicCreated || clinicUserRoles.length === 0) return null;

  const groupByClinicGroupName = groupBy(
    orderBy(
      clinicUserRoles,
      [(item) => groupByPath(item) === OTHER_GROUP_NAME, groupByPath],
      ['asc', 'asc']
    ),
    groupByPath
  );

  return (
    <div className="border-t-[0.1rem] border-light-grey w-full py-1.6 overflow-hidden">
      <div className="text-[1rem] font-bold text-mid-grey mb-0.8">
        PRACTICES
      </div>

      <div className="overflow-auto overscroll-contain scrollbar h-full">
        {!isGlobalAdmin ? (
          <ListClinic
            clinicUserRoles={clinicUserRoles}
            searchingValue={searchingValue}
            onClick={onClick}
          />
        ) : (
          Object.entries(groupByClinicGroupName).map(
            ([subdomain, clinicUserRoles]) => (
              <ExpandWrapper
                className="mt-1.6 mb-0.8 all-child:items-end"
                iconClassName="w-[20px] h-[20px] all-child:fill-mid-grey"
                key={subdomain}
                header={
                  subdomain && (
                    <div className="text-12 font-semibold text-mid-grey truncate w-full max-w-[24rem]">
                      {subdomain.toUpperCase()}
                    </div>
                  )
                }
              >
                <div>
                  <ListClinic
                    clinicUserRoles={clinicUserRoles}
                    searchingValue={searchingValue}
                    onClick={onClick}
                  />
                </div>
              </ExpandWrapper>
            )
          )
        )}
      </div>
    </div>
  );
};

export default ClinicOptions;
